import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap";
import { useInstallmentGroups } from "../hooks/useInstallmentGroups";
import { fetchGroupsPage } from "../../../api/requests/company-requests";
import InstallmentGroupModal from "./InstallmentGroupModal";
import GroupAccountsModal from "../../../pages/modals/accountGroup/GroupAccountsModal";
import PaginationBar from "../../../components/Common/PaginationBar";
import Select from "react-select";
import { fetchCompaniesNames } from "../../../api/requests/account-requests";
import { installmentLabel, MAX_INSTALLMENTS } from "../utils/formatters";

const styles = `
  .installment-groups-page .btn-orange { background: #E56A22; border-color: #E56A22; color: #fff; }
  .installment-groups-page .btn-orange:hover { filter: brightness(1.03); color: #fff; }
  .installment-groups-page .ig-table { margin-bottom: 0; font-size: .85rem; }
  .installment-groups-page .ig-table th {
    font-size: .75rem; font-weight: 600; color: #6b7280; background: #f8fafc;
    border-bottom: 1px solid #e5e7eb; white-space: nowrap; padding: .7rem .75rem;
  }
  .installment-groups-page .ig-table td { padding: .75rem; vertical-align: middle; border-color: #f1f3f5; }
  .installment-groups-page .ig-table .ig-name { font-weight: 700; color: #221C5D; }
  .installment-groups-page .ig-table .ig-muted { color: #6b7280; }
  .installment-groups-page .ig-table .ig-desc { max-width: 220px; }
  .installment-groups-page .ig-table .ig-actions { white-space: nowrap; }
  .installment-groups-page .ig-table .ig-installments { white-space: nowrap; }
  .installment-groups-page .ig-filters .form-control,
  .installment-groups-page .ig-filters .form-select { border-radius: 999px; font-size: .85rem; height: 31px; border-color: #ced4da; }
  .installment-groups-page .ig-filters .form-control:focus,
  .installment-groups-page .ig-filters .form-select:focus { border-color: #221C5D; box-shadow: none; }
  .installment-groups-page .corporate-pagination .page-link { color: #221C5D; border-color: #E56A22; }
  .installment-groups-page .corporate-pagination .page-item.active .page-link { background-color: #221C5D; border-color: #221C5D; color: #fff; }
  .installment-groups-page .corporate-pagination .page-link:hover { border-color: #221C5D; color: #221C5D; }
  .installment-groups-page .inst-pill {
    display: inline-block; border-radius: 999px; background: #eef2f8; color: #221C5D;
    font-size: .72rem; font-weight: 600; padding: .15rem .6rem; margin-right: .3rem;
  }
  .installment-groups-page .inst-pill.is-muted { background: #f1f3f5; color: #94a3b8; }
  .installment-groups-page .subscription-switch { padding-left: 2.4em; }
  .installment-groups-page .subscription-switch .form-check-input:checked {
    background-color: #E56A22; border-color: #E56A22;
  }
  .installment-groups-page .subscription-switch .form-check-label { cursor: pointer; }
  .installment-groups-page .group-scope-tabs { display: inline-flex; gap: .25rem; background: #f1f3f5; border-radius: 999px; padding: .2rem; }
  .installment-groups-page .group-scope-tab {
    border: 0; background: transparent; color: #64748b; font-size: .8rem; font-weight: 600;
    padding: .3rem .9rem; border-radius: 999px; transition: background .15s ease, color .15s ease;
  }
  .installment-groups-page .group-scope-tab:hover { color: #221C5D; }
  .installment-groups-page .group-scope-tab.is-active { background: #fff; color: #221C5D; box-shadow: 0 1px 2px rgba(15,23,42,.12); }
`;

/**
 * Bir grubun satışa açık taksit sayıları (marka farkı gözetmeden birleşik küme).
 *
 * Listede eskiden yalnız grup adı ve cari sayısı vardı; grubun hangi taksitleri
 * açtığını öğrenmek için modalı açmak gerekiyordu. Özet burada duruyor.
 */
const summarizeInstallments = (installmentRates = []) => {
    const set = new Set();
    installmentRates.forEach((r) => {
        if (r?.enabled !== false) set.add(Number(r.installmentCount));
    });
    return [...set].sort((a, b) => a - b);
};

const PAGE_SIZE = 10;
const FILTER_WIDTH = 220;

const SORT_OPTIONS = [
    { value: "createdAt,desc", label: "Oluşturma: Yeniden eskiye" },
    { value: "createdAt,asc", label: "Oluşturma: Eskiden yeniye" },
    { value: "companyName,asc", label: "Firma adı (A-Z)", allOnly: true },
    { value: "companyName,desc", label: "Firma adı (Z-A)", allOnly: true },
    { value: "groupName,asc", label: "Grup adı (A-Z)" },
];

// "Taksit grubu" tek firmanın listesinde istemcide sıralanır; tüm firmalarda sunucuda.
const sortLocally = (list, sort) => {
    const byDate = (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    const copy = [...list];
    if (sort === "createdAt,asc") return copy.sort(byDate);
    if (sort === "groupName,asc") return copy.sort((a, b) => (a.groupName || "").localeCompare(b.groupName || "", "tr"));
    return copy.sort((a, b) => byDate(b, a));
};

const formatCreatedAt = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("tr-TR", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
};

export default function InstallmentGroupsTab({ companyId, canManage = true, isSuperAdmin = false }) {
    const [activeSubTab, setActiveSubTab] = useState("own");
    const [selectedCompanyId, setSelectedCompanyId] = useState("");

    const browsingAllCompanies = isSuperAdmin && activeSubTab === "all";
    const effectiveCompanyId = browsingAllCompanies ? selectedCompanyId : companyId;

    const hookProps = useInstallmentGroups(effectiveCompanyId, canManage);

    // Hook'tan dönen tüm verileri destructuring ile alıyoruz
    const {
        groups, loading, modalOpen, toggleModal, openGroupModal,
        handleDeleteClick, confirmDelete, confirmModalOpen, setConfirmModalOpen,
        deletingGroup, toggleSubscriptionGroup, subscriptionLoadingId
    } = hookProps;

    const [accountsModalGroup, setAccountsModalGroup] = useState(null);
    const [search, setSearch] = useState("");

    const [allGroups, setAllGroups] = useState([]);
    const [allLoading, setAllLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [sort, setSort] = useState("createdAt,desc");
    const [companyFilter, setCompanyFilter] = useState("");
    const [companies, setCompanies] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const loadAllGroups = useCallback(async () => {
        if (!browsingAllCompanies) return;
        setAllLoading(true);
        try {
            const json = await fetchGroupsPage({ page, size: pageSize, searchTerm: search.trim(), companyId: companyFilter || undefined, sort });
            const body = json?.data || {};
            setAllGroups(body.content || []);
            setTotalPages(body.totalPages ?? 0);
            setTotalElements(body.totalElements ?? 0);
        } catch {
            setAllGroups([]);
            setTotalPages(0);
            setTotalElements(0);
        } finally {
            setAllLoading(false);
        }
    }, [browsingAllCompanies, page, pageSize, search, companyFilter, sort]);

    useEffect(() => {
        if (!browsingAllCompanies) return;
        const t = setTimeout(loadAllGroups, 350);
        return () => clearTimeout(t);
    }, [browsingAllCompanies, loadAllGroups]);

    useEffect(() => { setPage(0); }, [activeSubTab, search, pageSize, companyFilter, sort]);

    // Firma adına göre sıralama yalnız tüm firmalar listesinde anlamlı.
    useEffect(() => {
        if (!browsingAllCompanies && sort.startsWith("companyName")) setSort("createdAt,desc");
    }, [browsingAllCompanies, sort]);

    useEffect(() => {
        if (!browsingAllCompanies || companies.length > 0) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchCompaniesNames();
                if (!cancelled) setCompanies(res?.data || []);
            } catch {
                // Liste alınamazsa firma filtresi boş kalır; tablo yine çalışır.
            }
        })();
        return () => { cancelled = true; };
    }, [browsingAllCompanies, companies.length]);

    const companyOptions = useMemo(
        () => companies.map((c) => ({ value: c.id, label: c.companyName || c.name })),
        [companies]
    );

    // Tüm firmalar sekmesinden düzenleme: önce grubun firmasına geçilir, o firmanın
    // grupları yüklenince modal açılır. Tek adımda açmak grubu yanlış firmaya yazardı.
    const [pendingEditGroupId, setPendingEditGroupId] = useState(null);

    const editFromAllCompanies = (g) => {
        setSelectedCompanyId(g.companyId);
        setPendingEditGroupId(g.id);
    };

    useEffect(() => {
        if (!pendingEditGroupId) return;
        const target = groups.find((g) => g.id === pendingEditGroupId);
        if (target) {
            openGroupModal(target);
            setPendingEditGroupId(null);
        }
    }, [groups, pendingEditGroupId, openGroupModal]);

    // Düzenleme modalı kapanınca tüm firmalar listesi de tazelenir; yoksa kaydedilen
    // değişiklik yalnız arkadaki tek firma listesine yansır, ekranda eski hâli kalır.
    useEffect(() => {
        if (browsingAllCompanies && !modalOpen && selectedCompanyId) {
            loadAllGroups();
        }
    }, [modalOpen]); // eslint-disable-line

    // Silme grup kimliğiyle çalışıyor, firmadan bağımsız. Ama hook yalnız kendi
    // bağlı olduğu firmanın listesini tazeliyor; tüm firmalar görünümündeki liste
    // ayrı bir kaynak olduğu için onu da elle yenilemek gerekiyor.
    const handleConfirmDelete = async () => {
        await confirmDelete();
        if (browsingAllCompanies) loadAllGroups();
    };

    const visibleGroups = useMemo(() => {
        if (browsingAllCompanies) return allGroups; // arama ve sıralama sunucuda yapıldı
        const q = search.trim().toLocaleLowerCase("tr");
        const filtered = q
            ? groups.filter((g) =>
                `${g.groupName || ""} ${g.description || ""}`.toLocaleLowerCase("tr").includes(q))
            : groups;
        return sortLocally(filtered, sort);
    }, [browsingAllCompanies, allGroups, groups, search, sort]);

    const listedCount = browsingAllCompanies ? totalElements : visibleGroups.length;
    const rowOffset = browsingAllCompanies ? page * pageSize : 0;

    const listLoading = browsingAllCompanies ? allLoading : loading;

    return (
        <div className="installment-groups-page">
            <style>{styles}</style>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                    <h3 className="mb-1">Taksit Grupları</h3>
                    <div className="text-muted small">
                        Cari gruplarına özel taksit seçeneklerini ve komisyon oranlarını belirleyin.
                    </div>

                    {isSuperAdmin && (
                        <div className="group-scope-tabs mt-2">
                            <button
                                type="button"
                                className={`group-scope-tab ${activeSubTab === "own" ? "is-active" : ""}`}
                                onClick={() => setActiveSubTab("own")}
                            >
                                Taksit Gruplarımız
                            </button>
                            <button
                                type="button"
                                className={`group-scope-tab ${activeSubTab === "all" ? "is-active" : ""}`}
                                onClick={() => setActiveSubTab("all")}
                            >
                                Tüm Taksit Grupları
                                {totalElements > 0 && activeSubTab === "all" && (
                                    <span className="ms-1">({totalElements})</span>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <div className="ig-filters d-flex align-items-center gap-2 flex-wrap">
                    {browsingAllCompanies && (
                        <div style={{ width: FILTER_WIDTH }}>
                            <Select
                                options={companyOptions}
                                value={companyOptions.find((o) => o.value === companyFilter) || null}
                                onChange={(opt) => setCompanyFilter(opt?.value ?? "")}
                                placeholder="Tüm firmalar"
                                isClearable
                                noOptionsMessage={() => "Firma bulunamadı"}
                                maxMenuHeight={228}
                                styles={{
                                    control: (base, state) => ({
                                        ...base, borderRadius: 999, minHeight: 31, height: 31, fontSize: ".85rem",
                                        borderColor: state.isFocused ? "#221C5D" : "#ced4da",
                                        boxShadow: "none",
                                        "&:hover": { borderColor: state.isFocused ? "#221C5D" : "#ced4da" },
                                    }),
                                    placeholder: (base) => ({ ...base, color: "#6c757d" }),
                                    indicatorSeparator: () => ({ display: "none" }),
                                    valueContainer: (base) => ({ ...base, padding: "0 .75rem" }),
                                    indicatorsContainer: (base) => ({ ...base, height: 29 }),
                                    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
                                    clearIndicator: (base) => ({ ...base, padding: 4 }),
                                    menu: (base) => ({ ...base, zIndex: 20, fontSize: ".85rem" }),
                                    menuList: (base) => ({ ...base, overscrollBehavior: "contain" }),
                                }}
                            />
                        </div>
                    )}
                    {(groups.length > 0 || browsingAllCompanies) && (
                        <Input
                            bsSize="sm"
                            type="search"
                            placeholder="Grup ara..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ width: FILTER_WIDTH }}
                        />
                    )}
                    {(groups.length > 0 || browsingAllCompanies) && (
                        <Input
                            bsSize="sm"
                            type="select"
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            style={{ width: FILTER_WIDTH }}
                            aria-label="Sıralama"
                        >
                            {SORT_OPTIONS.filter((o) => browsingAllCompanies || !o.allOnly).map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </Input>
                    )}
                    <Button
                        className="btn-navy btn-rounded"
                        onClick={() => openGroupModal(null)}
                        disabled={listLoading || browsingAllCompanies}
                        title={browsingAllCompanies
                            ? "Grup oluşturmak için önce bir firma seçin"
                            : "Yeni taksit grubu oluştur"}
                    >
                        <i className="bx bx-plus me-1"></i> Taksit Grubu Oluştur
                    </Button>
                </div>
            </div>

            {browsingAllCompanies && (
                <div className="alert alert-light border d-flex align-items-center gap-2 py-2 px-3 mb-3">
                    <i className="bx bx-info-circle" />
                    <span className="small">
                        Tüm firmaların taksit grupları listeleniyor. Bir grubu düzenlemek için satırın sağındaki düzenle tuşunu kullanın.
                    </span>
                </div>
            )}

            {(groups.length > 0 || browsingAllCompanies) && (
                <div className="small fw-semibold text-dark mb-2">
                    {listedCount} taksit grubu listeleniyor
                </div>
            )}

            {listLoading && visibleGroups.length === 0 ? (
                <div className="text-center p-4">
                    <Spinner color="primary" size="sm" />
                    <span className="ms-2 text-muted">Gruplar yükleniyor...</span>
                </div>
            ) : (
                <div className="table-responsive shadow-sm rounded-4 border bg-white">
                    <table className="table table-hover ig-table">
                        <thead>
                            <tr>
                                <th style={{ width: 48 }}>#</th>
                                <th>Grup Adı</th>
                                {browsingAllCompanies && <th>Firma</th>}
                                <th>Taksit Seçenekleri</th>
                                <th>Açıklama</th>
                                <th>Oluşturma Tarihi</th>
                                <th className="text-end">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                    {visibleGroups.map((g, idx) => {
                        const installments = summarizeInstallments(g.installmentRates);
                        return (
                            <tr key={g.id}>
                                <td className="ig-muted">{rowOffset + idx + 1}</td>
                                <td>
                                    <div className="d-flex align-items-center flex-wrap gap-1">
                                        <span className="ig-name me-1">{g.groupName}</span>
                                        <Badge
                                            className="fw-normal"
                                            style={{
                                                borderRadius: 999,
                                                backgroundColor: (g.accountCount ?? 0) > 0 ? "#221C5D" : "#B0B7C3",
                                            }}
                                            title="Bu taksit grubuna bağlı cari sayısı"
                                        >
                                            {g.accountCount ?? 0} cari
                                        </Badge>
                                        {isSuperAdmin && g.companyId === companyId && (
                                            <Badge
                                                className="fw-normal"
                                                style={{ borderRadius: 999, backgroundColor: "#0f766e" }}
                                                title="Bu grup platform firmasına (bize) ait"
                                            >
                                                Bize ait
                                            </Badge>
                                        )}
                                        {g.subscriptionGroup && (
                                            <Badge
                                                className="fw-normal"
                                                style={{ borderRadius: 999, backgroundColor: "#E56A22" }}
                                                title="Paket (abonelik) ödemelerinde bu grubun taksitleri gösteriliyor"
                                            >
                                                <i className="bx bx-package me-1"></i> Paket ödemesi
                                            </Badge>
                                        )}
                                    </div>
                                </td>
                                {browsingAllCompanies && (
                                    <td className="ig-muted">{g.companyName || "Firma bilinmiyor"}</td>
                                )}
                                <td className="ig-installments">
                                    {/* Tüm taksitler açıksa 12 rozet yerine tek rozet. */}
                                    {!installments.length && (
                                        <span className="inst-pill is-muted">Satışa açık taksit yok</span>
                                    )}
                                    {installments.length === MAX_INSTALLMENTS && (
                                        <span className="inst-pill">Tüm taksitler (1-{MAX_INSTALLMENTS})</span>
                                    )}
                                    {installments.length > 0 && installments.length < MAX_INSTALLMENTS && (
                                        installments.slice(0, 3).map((i) => (
                                            <span key={i} className="inst-pill">{installmentLabel(i)}</span>
                                        ))
                                    )}
                                    {installments.length > 3 && installments.length < MAX_INSTALLMENTS && (
                                        <span
                                            className="inst-pill is-muted"
                                            title={installments.slice(3).map(installmentLabel).join(", ")}
                                        >
                                            +{installments.length - 3}
                                        </span>
                                    )}
                                </td>
                                <td className="ig-muted ig-desc">
                                    <div className="text-truncate" title={g.description || undefined}>
                                        {g.description || "-"}
                                    </div>
                                </td>
                                <td className="ig-muted text-nowrap">{formatCreatedAt(g.createdAt)}</td>
                                <td className="ig-actions">
                                <div className="d-flex gap-2 align-items-center justify-content-end">
                                    {/* Tüm firmalar görünümü salt okunur: eylemler seçili firmaya
                                        bağlı çalışıyor, firma seçilmeden gösterilmeleri yanıltıcı olur. */}
                                    {/* ABONELİK TAKSİT GRUBU — paket ödemesini platform kendi
                                        tahsil ettiği için taksit seçenekleri ödeyen firmanın
                                        değil, sistem genelinde açık olan bu tek grubun
                                        oranlarından gelir. Uç yalnız SUPER_ADMIN'e açık;
                                        diğer rollere gösterip 403 aldırmak yerine gizleniyor. */}
                                    {isSuperAdmin && !browsingAllCompanies && (
                                        <div
                                            className="form-check form-switch subscription-switch me-1"
                                            title={
                                                g.subscriptionGroup
                                                    ? "Paket ödemelerinde bu grubun taksitleri gösteriliyor"
                                                    : "Paket ödemelerinde bu grubun taksitlerini kullan"
                                            }
                                        >
                                            <Input
                                                type="switch"
                                                role="switch"
                                                id={`subscription-group-${g.id}`}
                                                checked={Boolean(g.subscriptionGroup)}
                                                disabled={subscriptionLoadingId === g.id}
                                                onChange={(e) =>
                                                    toggleSubscriptionGroup(g, e.target.checked)
                                                }
                                            />
                                            <label
                                                className="form-check-label small text-muted"
                                                htmlFor={`subscription-group-${g.id}`}
                                            >
                                                {subscriptionLoadingId === g.id ? (
                                                    <Spinner size="sm" color="secondary" />
                                                ) : (
                                                    "Paket ödemesi"
                                                )}
                                            </label>
                                        </div>
                                    )}

                                    {browsingAllCompanies && (
                                        <>
                                            <Button
                                                size="sm"
                                                className="btn-orange btn-rounded"
                                                onClick={() => setAccountsModalGroup(g)}
                                            >
                                                <i className="bx bx-group me-1"></i> Bağlı Cariler
                                            </Button>
                                            <Button
                                                size="sm"
                                                className="btn-outline-navy btn-rounded"
                                                onClick={() => editFromAllCompanies(g)}
                                                disabled={pendingEditGroupId === g.id}
                                                title={`${g.companyName || "Firma"} firmasına geçip bu grubu düzenle`}
                                            >
                                                {pendingEditGroupId === g.id ? (
                                                    <Spinner size="sm" color="secondary" />
                                                ) : (
                                                    <><i className="bx bx-edit me-1"></i> Düzenle</>
                                                )}
                                            </Button>
                                        </>
                                    )}

                                    {!browsingAllCompanies && (
                                        <>
                                            {/* BAĞLI CARİLER (SALT OKUNUR): Orange & Rounded */}
                                            <Button
                                                size="sm"
                                                className="btn-orange btn-rounded"
                                                onClick={() => setAccountsModalGroup(g)}
                                            >
                                                <i className="bx bx-group me-1"></i> Bağlı Cariler
                                            </Button>

                                            {/* DÜZENLEME BUTONU: Outline Navy & Rounded */}
                                            <Button
                                                size="sm"
                                                className="btn-outline-navy btn-rounded"
                                                onClick={() => openGroupModal(g)}
                                            >
                                                <i className="bx bx-edit me-1"></i> Düzenle
                                            </Button>
                                        </>
                                    )}

                                    {/* SİLME BUTONU — silme ucu yalnız SUPER_ADMIN/COMPANY_ADMIN'e
                                        açık; muhasebeciye gösterilip 403 aldırmak yerine gizleniyor. */}
                                    {canManage && (
                                        <Button
                                            size="sm"
                                            color="danger"
                                            className="btn-rounded"
                                            onClick={() => handleDeleteClick(g)}
                                        >
                                            <i className="bx bx-trash me-1"></i> Sil
                                        </Button>
                                    )}
                                </div>
                                </td>
                            </tr>
                        );
                    })}
                        </tbody>
                    </table>

                    {!visibleGroups.length && !listLoading && (
                        <div className="text-center p-4 text-muted bg-light rounded-4">
                            <i className="bx bx-layer display-6 mb-2"></i>
                            <p className="mb-0">
                                {search
                                    ? "Aramanıza uyan taksit grubu bulunamadı."
                                    : "Henüz bir taksit grubu oluşturulmamış."}
                            </p>
                        </div>
                    )}
                </div>
            )}


            {browsingAllCompanies && (
                <PaginationBar
                    currentPage={page}
                    totalPages={totalPages}
                    totalElements={totalElements}
                    pageSize={pageSize}
                    onPageChange={(p) => { if (!allLoading && p >= 0 && p < totalPages) setPage(p); }}
                    onPageSizeChange={setPageSize}
                />
            )}

            {/* BAĞLI CARİLER MODALI (salt okunur) */}
            <GroupAccountsModal
                isOpen={!!accountsModalGroup}
                toggle={() => setAccountsModalGroup(null)}
                group={accountsModalGroup}
            />

            {/* Büyük Edit/Create Modalı */}
            <InstallmentGroupModal
                isOpen={modalOpen}
                toggle={toggleModal}
                hookProps={hookProps}
            />

            {/* SİLME ONAY MODALI */}
            <Modal
                isOpen={confirmModalOpen}
                toggle={() => setConfirmModalOpen(false)}
                centered
            >
                <ModalHeader toggle={() => setConfirmModalOpen(false)} className="border-0 pb-0">
                    Silme Onayı
                </ModalHeader>
                <ModalBody className="pt-3">
                    <p className="mb-2">
                        <strong>{deletingGroup?.groupName}</strong> grubunu silmek istediğinize emin misiniz?
                    </p>
                    {/* Silme, gruba bağlı carileri sessizce kopartıyor (backend
                        clearGroupByGroupId). Sayı burada yazmazsa kullanıcı kaybı
                        ancak sonradan fark ediyor. */}
                    {(deletingGroup?.accountCount ?? 0) > 0 && (
                        <p className="text-danger mb-2">
                            <i className="bx bx-error-circle me-1" />
                            Bu gruba bağlı <strong>{deletingGroup.accountCount} cari</strong> gruptan
                            çıkarılacak ve taksit seçenekleri olmadan kalacak.
                        </p>
                    )}
                    <p className="text-muted small mb-0">Bu işlem geri alınamaz.</p>
                </ModalBody>
                <ModalFooter className="border-0 pt-0">
                    <Button
                        color="secondary"
                        outline
                        className="btn-rounded"
                        onClick={() => setConfirmModalOpen(false)}
                        disabled={loading}
                    >
                        İptal
                    </Button>
                    <Button
                        color="danger"
                        className="btn-rounded"
                        onClick={handleConfirmDelete}
                        disabled={loading}
                    >
                        {loading ? <Spinner size="sm" /> : <i className="bx bx-trash me-1"></i>} Sil
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
